import React, { useState } from 'react';
import { Mail, MapPin, MessageCircle, Calendar, Clock } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '', company: '', country: '', email: '', whatsapp: '', projectType: '',
    squareMeters: '', timeline: '', budget: '', location: '', message: '', consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const constructionTypes = ['Vivienda unifamiliar', 'Vivienda multifamiliar', 'Industrial / Nave', 'Oil & Gas / Minería', 'Hospitales / Escuelas', 'Hoteles', 'Oficinas', 'Otro'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', company: '', country: '', email: '', whatsapp: '', projectType: '', squareMeters: '', timeline: '', budget: '', location: '', message: '', consent: false });
    }, 3000);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section className="relative pt-40 pb-24 bg-neutral-950 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
            <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">Contacto</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              Cotiza tu proyecto
              <span className="block text-red-500">en 24 horas</span>
            </h1>
            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">Revolucionamos la construcción: rápido, accesible y de calidad internacional.</p>
          </div>
        </section>

        <section className="py-24 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-1">
                <h2 className="text-2xl font-bold text-neutral-900 mb-8">Información de Contacto</h2>
                <div className="space-y-6">
                  <a href="mailto:modular@wedevelop.one" className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-red-600" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900">Email</p>
                      <p className="text-neutral-600">modular@wedevelop.one</p>
                    </div>
                  </a>
                  <a href="https://wa.me/5491112345678" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-green-600" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900">WhatsApp</p>
                      <p className="text-neutral-600">Chat directo</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900">Ubicación</p>
                      <p className="text-neutral-600">Buenos Aires, Argentina</p>
                      <p className="text-neutral-600">Montevideo, Uruguay</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Clock className="text-purple-600" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900">Horario</p>
                      <p className="text-neutral-600">Lun - Vie: 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 space-y-4">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white py-6">
                    <Calendar className="mr-2" size={20} />Agendar Llamada
                  </Button>
                  <Button variant="outline" className="w-full border-neutral-300 py-6" asChild>
                    <a href="https://wa.me/5491112345678" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2" size={20} />Escribir por WhatsApp
                    </a>
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm">
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">Solicitar Propuesta</h2>
                  <p className="text-neutral-600 mb-8">Completá el formulario y te respondemos en menos de 24 horas.</p>
                  
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <h3 className="text-2xl font-bold text-neutral-900 mb-2">¡Gracias por contactarnos!</h3>
                      <p className="text-neutral-600">Nos comunicaremos contigo en las próximas 24 horas.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nombre *</Label>
                          <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Tu nombre" required className="bg-white border-neutral-200" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Empresa *</Label>
                          <Input id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Nombre de la empresa" required className="bg-white border-neutral-200" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="country">País *</Label>
                          <select id="country" name="country" value={formData.country} onChange={handleChange} required className="w-full h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm">
                            <option value="">Seleccionar país</option>
                            <option value="Argentina">Argentina</option>
                            <option value="Uruguay">Uruguay</option>
                            <option value="Chile">Chile</option>
                            <option value="Otro">Otro</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="tu@email.com" required className="bg-white border-neutral-200" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="whatsapp">WhatsApp</Label>
                          <Input id="whatsapp" name="whatsapp" value={formData.whatsapp} onChange={handleChange} placeholder="+54 9 11 1234 5678" className="bg-white border-neutral-200" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="projectType">Tipo de Construcción *</Label>
                          <select id="projectType" name="projectType" value={formData.projectType} onChange={handleChange} required className="w-full h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm">
                            <option value="">Seleccionar tipo</option>
                            {constructionTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="squareMeters">m² o Unidades</Label>
                          <Input id="squareMeters" name="squareMeters" value={formData.squareMeters} onChange={handleChange} placeholder="Ej: 5000 m²" className="bg-white border-neutral-200" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="timeline">Plazo Estimado</Label>
                          <Input id="timeline" name="timeline" value={formData.timeline} onChange={handleChange} placeholder="Ej: 6 meses" className="bg-white border-neutral-200" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Mensaje</Label>
                        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Contános más sobre tu proyecto..." rows={4} className="bg-white border-neutral-200" />
                      </div>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" id="consent" name="consent" checked={formData.consent} onChange={handleChange} required className="mt-1 rounded border-neutral-300 text-red-600" />
                        <label htmlFor="consent" className="text-sm text-neutral-600">Acepto recibir información de We Develop.</label>
                      </div>
                      <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white py-6" disabled={isSubmitting}>
                        {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
